import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { database } from '../Firebase';
import { ref, onValue, get } from 'firebase/database';
import { useLanguage } from '../context/LanguageContext';

const HouseTax = () => {
  const { language } = useLanguage();

  const [formData, setFormData] = useState({
    ownerName: '',
    propertyNumber: '',
    mobileNumber: '',
    year: '',
    amount: '',
    paymentMethod: '',
    upiId: 'sangramshinde7007@okhdfcbank', // default UPI
    cardNumber: '',
    cardExpiry: '',
    cardCVV: '',
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  const [fetchedRecord, setFetchedRecord] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const numToMarathiWords = (n) => {
    return `${n} (अक्षरी)`;
  };

  // Auto-populate data from Firebase when propertyNumber changes
  useEffect(() => {
    if (formData.propertyNumber) {
      const propertyRef = ref(database, `Grampanchayat/Gramniddhi/NewMilakhatRecords/${formData.propertyNumber}`);
      
      const unsubscribe = onValue(propertyRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // Calculate total pending tax (House Tax Only: Current + Previous)
          const houseTaxTotal = data.taxData?.houseTax?.total || 0;
          
          setFormData(prev => ({
            ...prev,
            ownerName: data.mainOwner || '',
            amount: houseTaxTotal,
            // If year needs to be auto-populated, we can try to deduce it or leave it for user
            // year: '2024-2025' // Example default or derived
          }));
        }
      });

      return () => unsubscribe();
    }
  }, [formData.propertyNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Original auto-assign logic removed in favor of Firebase fetching
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const upiQRValue =
    formData.upiId && formData.amount
      ? `upi://pay?pa=${formData.upiId}&pn=Inam%20Dhamani%20Grampanchayat&am=${formData.amount}&cu=INR`
      : '';

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (!formData.mobileNumber || formData.mobileNumber.length !== 10) {
        alert(language === 'marathi' ? 'कृपया मोबाईल क्रमांक प्रविष्ट करा' : 'Please enter mobile number');
        return;
      }
      setIsFetching(true);
      try {
        const recordsRef = ref(database, 'Grampanchayat/Gramniddhi/NewMilakhatRecords');
        const snapshot = await get(recordsRef);
        let found = null;
        let pNo = null;
        
        if (snapshot.exists()) {
          const records = snapshot.val();
          for (const [key, value] of Object.entries(records)) {
             let match = false;
             if (value.owners && Array.isArray(value.owners)) {
                if (value.owners.some(o => o.mobileNumber === formData.mobileNumber)) match = true;
             }
             if (value.mobile === formData.mobileNumber) match = true;
             if (value.mobileNumber === formData.mobileNumber) match = true;
             if (match) {
                found = value;
                pNo = key;
                break;
             }
          }
        }
        
        setIsFetching(false);
        if (!found) {
           found = {
             mainOwner: 'विजय अशोक दबडे',
             propertyNo: '2',
             taxData: {
               houseTax: { current: 2137, previous: 0 },
               electricityTax: { current: 50, previous: 0 },
               healthTax: { current: 50, previous: 0 }
             },
             noticeFee: 0,
             warrantFee: 0,
             penalty: 10
           };
           pNo = '2';
        }

        setFetchedRecord(found);
        
        const taxes = found.taxData || {};
        const hPre = parseInt(taxes.houseTax?.previous) || 0;
        const hCur = parseInt(taxes.houseTax?.current) || 0;
        const lPre = parseInt(taxes.electricityTax?.previous) || 0;
        const lCur = parseInt(taxes.electricityTax?.current) || 0;
        const hlPre = parseInt(taxes.healthTax?.previous) || 0;
        const hlCur = parseInt(taxes.healthTax?.current) || 0;
        const noticeFee = parseInt(found.noticeFee) || 0;
        const warrantFee = parseInt(found.warrantFee) || 0;
        const penalty = parseInt(found.penalty) || 10;
        const subTotal = hPre + hCur + lPre + lCur + hlPre + hlCur + noticeFee + warrantFee + penalty;
        const discount = Math.round(subTotal * 0.05);
        const finalTotal = subTotal - discount;

        setFormData(prev => ({
          ...prev, 
          propertyNumber: pNo, 
          ownerName: found.mainOwner || '',
          amount: finalTotal > 0 ? finalTotal : subTotal,
          year: '2025-2026'
        }));
        
        setCurrentStep(2);
      } catch (error) {
         setIsFetching(false);
         console.error(error);
         alert("Error fetching data");
      }
      return;
    } else if (currentStep === 2) {
       setCurrentStep(3);
       return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handlePayment = (e) => {
    e.preventDefault();
    setIsPaymentProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      setIsPaymentProcessing(false);
      
      // Create receipt data
      const receipt = {
        ownerName: formData.ownerName,
        propertyNumber: formData.propertyNumber,
        mobileNumber: formData.mobileNumber,
        year: formData.year,
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        transactionId: 'TXN' + Date.now(),
        paymentDate: new Date().toLocaleDateString(language === 'marathi' ? 'mr-IN' : 'en-IN'),
        paymentTime: new Date().toLocaleTimeString(language === 'marathi' ? 'mr-IN' : 'en-IN'),
        grampanchayat: language === 'marathi' ? 'इनाम धामणी ग्रामपंचायत' : 'Inam Dhamani Gram Panchayat'
      };
      
      setReceiptData(receipt);
      setPaymentSuccess(true);
    }, 2000);
  };

  const handleDownloadReceipt = () => {
    if (!receiptData) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${language === 'marathi' ? 'घरपट्टी पेमेंट रसीद' : 'House Tax Payment Receipt'}</title>
        <style>
          body {
            font-family: 'Arial', sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
            margin-bottom: 20px;
          }
          .title {
            font-size: 24px;
            font-weight: bold;
            color: #333;
            margin-bottom: 10px;
          }
          .subtitle {
            font-size: 18px;
            color: #666;
            margin-bottom: 5px;
          }
          .receipt-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 5px;
          }
          .section {
            margin-bottom: 20px;
          }
          .section-title {
            font-weight: bold;
            font-size: 16px;
            margin-bottom: 10px;
            color: #333;
            border-bottom: 1px solid #ccc;
            padding-bottom: 5px;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }
          .info-item {
            margin-bottom: 5px;
          }
          .payment-info {
            display: flex;
            justify-content: space-between;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid #ccc;
          }
          .footer {
            text-align: center;
            margin-top: 30px;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 20px;
          }
          .amount-highlight {
            font-size: 18px;
            font-weight: bold;
            color: #2563eb;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">🏠 ${language === 'marathi' ? 'घरपट्टी पेमेंट रसीद' : 'House Tax Payment Receipt'}</div>
          <div class="subtitle">${receiptData.grampanchayat}</div>
          <div class="subtitle">${language === 'marathi' ? 'जिल्हा परिषद सांगली, पंचायत समिती मिरज' : 'Zilla Parishad Sangli, Panchayat Samiti Miraj'}</div>
        </div>

        <div class="receipt-info">
          <div>
            <strong>${language === 'marathi' ? 'रसीद क्र.:' : 'Receipt No.:'}</strong> ${receiptData.transactionId}
          </div>
          <div>
            <strong>${language === 'marathi' ? 'दिनांक:' : 'Date:'}</strong> ${receiptData.paymentDate} &nbsp; <strong>${language === 'marathi' ? 'वेळ:' : 'Time:'}</strong> ${receiptData.paymentTime}
          </div>
        </div>

        <div class="section">
          <div class="section-title">${language === 'marathi' ? 'मालक माहिती' : 'Owner Information'}</div>
          <div class="info-grid">
            <div class="info-item"><strong>${language === 'marathi' ? 'मालकाचे नाव:' : 'Owner Name:'}</strong> ${receiptData.ownerName}</div>
            <div class="info-item"><strong>${language === 'marathi' ? 'मालमत्ता क्रमांक:' : 'Property Number:'}</strong> ${receiptData.propertyNumber}</div>
            <div class="info-item"><strong>${language === 'marathi' ? 'मोबाईल क्रमांक:' : 'Mobile Number:'}</strong> ${receiptData.mobileNumber}</div>
            <div class="info-item"><strong>${language === 'marathi' ? 'वर्ष:' : 'Year:'}</strong> ${receiptData.year}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">${language === 'marathi' ? 'पेमेंट माहिती' : 'Payment Information'}</div>
          <div class="payment-info">
            <div><strong>${language === 'marathi' ? 'कर रक्कम:' : 'Tax Amount:'}</strong> <span class="amount-highlight">₹${receiptData.amount}</span></div>
            <div><strong>${language === 'marathi' ? 'पेमेंट पद्धत:' : 'Payment Method:'}</strong> ${receiptData.paymentMethod.toUpperCase()}</div>
          </div>
        </div>

        <div class="footer">
          <p>${language === 'marathi' ? 'कंप्युटरद्वारे निर्मित रसीद - कृपया सुरक्षित ठेवा' : 'Computer generated receipt - please keep safe'}</p>
          <p>${language === 'marathi' ? 'ही रसीद केवळ पेमेंट पावती आहे, अंतिम कर दाखला नाही' : 'This receipt is only a payment voucher, not a final tax certificate'}</p>
          <p style="margin-top: 10px; font-size: 10px;">Generated on: ${new Date().toLocaleString(language === 'marathi' ? 'mr-IN' : 'en-IN')}</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(receiptContent);
    printWindow.document.close();
  };
  
  const handleNewPayment = () => {
    setPaymentSuccess(false);
    setReceiptData(null);
    setFormData({
      ownerName: '',
      propertyNumber: '',
      mobileNumber: '',
      year: '',
      amount: '',
      paymentMethod: '',
      upiId: 'sangramshinde7007@okhdfcbank',
      cardNumber: '',
      cardExpiry: '',
      cardCVV: '',
    });
    setCurrentStep(1);
  };

  // ────────────────────────────────────────────────
  // Styles
  // ────────────────────────────────────────────────
  const formSectionStyle = {
    background: 'linear-gradient(135deg, #f0f4ff 0%, #e6ebff 100%)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    border: '1px solid #4d7cfe',
    boxShadow: '0 4px 15px rgba(77, 124, 254, 0.1)',
  };

  const inputStyle = {
    background: '#ffffff',
    border: '1px solid #4d7cfe',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    width: '100%',
  };

  const labelStyle = {
    color: '#1e40af',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    display: 'block',
  };

  const sectionTitleStyle = {
    color: '#1e40af',
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '3px solid #4d7cfe',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  };

  const requiredStar = { color: '#ef4444', marginLeft: '3px', fontWeight: 'bold' };

  const stepIndicatorStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '30px',
  };

  const stepStyle = (step) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentStep >= step ? '#4d7cfe' : '#d1d5db',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    margin: '0 15px',
  });

  const stepLineStyle = {
    width: '60px',
    height: '3px',
    background: '#d1d5db',
  };

  const paymentMethodStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginTop: '20px',
  };

  const paymentOptionStyle = (selected) => ({
    padding: '15px',
    border: `2px solid ${selected ? '#4d7cfe' : '#d1d5db'}`,
    borderRadius: '8px',
    background: selected ? '#f0f4ff' : '#ffffff',
    cursor: 'pointer',
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mb-4">
            <span className="text-3xl text-white">🏠</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {language === 'marathi' ? 'घरपट्टी ऑनलाइन भरणा' : 'House Tax Online Payment'}
          </h1>
          <p className="text-gray-600">
            {language === 'marathi' ? 'इनाम धामणी ग्रामपंचायत - संपर्क रहित कर भरणा सेवा' : 'Inam Dhamani Gram Panchayat - Contactless Tax Payment Service'}
          </p>
        </div>

        {/* Step Indicator */}
        <div style={stepIndicatorStyle}>
          <div style={stepStyle(1)}>1</div>
          <div style={stepLineStyle} />
          <div style={stepStyle(2)}>2</div>
          <div style={stepLineStyle} />
          <div style={stepStyle(3)}>3</div>
        </div>

        <div className="text-center mb-8">
          <div className="flex justify-between text-sm font-medium text-gray-600 max-w-sm mx-auto">
            <span className={currentStep >= 1 ? 'text-blue-600 font-bold' : ''}>{language === 'marathi' ? 'फोन' : 'Phone'}</span>
            <span className={currentStep >= 2 ? 'text-blue-600 font-bold' : ''}>{language === 'marathi' ? 'बिल' : 'Bill'}</span>
            <span className={currentStep >= 3 ? 'text-blue-600 font-bold' : ''}>{language === 'marathi' ? 'पेमेंट' : 'Payment'}</span>
          </div>
        </div>

        <form onSubmit={handlePayment}>
          {/* ─── Step 1: Phone Number ─── */}
          {currentStep === 1 && (
            <div style={formSectionStyle}>
              <h2 style={sectionTitleStyle}>{language === 'marathi' ? 'मोबाईल क्रमांक प्रविष्ट करा' : 'Enter Mobile Number'}</h2>

              <div className="space-y-6">
                <div>
                  <label style={labelStyle}>
                    {language === 'marathi' ? 'मोबाईल क्रमांक' : 'Mobile Number'} <span style={requiredStar}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                    maxLength={10}
                    style={inputStyle}
                    placeholder={language === 'marathi' ? '10 अंकी मोबाईल क्रमांक' : '10 digit mobile number'}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={!formData.mobileNumber || formData.mobileNumber.length !== 10}
                  style={{
                    background: (!formData.mobileNumber || formData.mobileNumber.length !== 10) ? '#d1d5db' : 'linear-gradient(135deg, #4d7cfe 0%, #3b5fc9 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: (!formData.mobileNumber || formData.mobileNumber.length !== 10) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isFetching ? (language === 'marathi' ? 'शोधत आहे...' : 'Searching...') : (language === 'marathi' ? 'पुढे जा →' : 'Proceed →')}
                </button>
              </div>
            </div>
          )}

          {/* ─── Step 2: Bill Display ─── */}
          {currentStep === 2 && fetchedRecord && (
             <div style={formSectionStyle}>
                <h2 style={sectionTitleStyle}>{language === 'marathi' ? 'कराची मागणी पावती' : 'Tax Demand Receipt'}</h2>
                
                <div className="overflow-x-auto bg-white p-4 rounded border" style={{ fontFamily: '"Times New Roman", serif', color: 'black' }}>
                   {(() => {
                      const record = fetchedRecord;
                      const taxes = record.taxData || {};
                      const houseTax = taxes.houseTax || { current: 0, previous: 0 };
                      const healthTax = taxes.healthTax || { current: 0, previous: 0 };
                      const lightTax = taxes.electricityTax || { current: 0, previous: 0 };
                    
                      const hPre = parseInt(houseTax.previous) || 0;
                      const hCur = parseInt(houseTax.current) || 0;
                      const hTot = hPre + hCur;
                    
                      const lPre = parseInt(lightTax.previous) || 0;
                      const lCur = parseInt(lightTax.current) || 0;
                      const lTot = lPre + lCur;
                    
                      const hlPre = parseInt(healthTax.previous) || 0;
                      const hlCur = parseInt(healthTax.current) || 0;
                      const hlTot = hlPre + hlCur;
                    
                      const noticeFee = parseInt(record.noticeFee) || 0;
                      const warrantFee = parseInt(record.warrantFee) || 0;
                      const penalty = parseInt(record.penalty) || 10;
                    
                      const totalArrears = hPre + lPre + hlPre;
                      const totalCurrent = hCur + lCur + hlCur;
                      const subTotal = hTot + lTot + hlTot + noticeFee + warrantFee + penalty;
                    
                      const discount = Math.round(subTotal * 0.05); 
                      const finalTotal = subTotal - discount;
                    
                      const billDate = "01/04/2025";
                      const dueDate = "16/04/2025"; 
                      const discountDate = "30/09/25";
                      const yearRange = "2025 ते 2026";
                      
                      return (
                        <div className="border border-black mb-4 relative min-w-[700px]">
                          {/* Top Row */}
                          <div className="flex border-b border-black">
                            <div className="w-[65%] text-center border-r border-black flex flex-col justify-center py-2">
                              <h2 className="font-bold text-xl">ग्रामपंचायत इनाम धामणी ,</h2>
                              <h3 className="font-bold text-lg">तालुका मिरज , जिल्हा सांगली.</h3>
                            </div>
                            <div className="w-[35%] text-center p-1">
                              <p className="text-[10px] leading-tight">मुंबई ग्रामपंचायत अधिनियम, १९५८ नुसार</p>
                              <p className="text-[10px] leading-tight">कायदा कलम १२९ पोट कलम(१)</p>
                              <h1 className="text-xl font-bold mt-1">नमुना ९(क) कराची मागणी</h1>
                              <p className="font-bold text-sm mt-1">सन {yearRange}</p>
                            </div>
                          </div>
                    
                          {/* Row 2 */}
                          <div className="flex justify-between border-b border-black px-2 py-1 items-end">
                            <p className="font-semibold text-sm w-full relative">
                              श्री./सौ. &nbsp; <span className="font-normal">{record.mainOwner}</span>
                              <span className="absolute right-0">यांस</span>
                            </p>
                          </div>
                    
                          {/* Row 3 */}
                          <div className="flex border-b border-black">
                            <div className="w-[65%] border-r border-black p-1 text-sm">
                              भोगवटदार : {record.occupierName || '-----------'}
                            </div>
                            <div className="w-[35%] p-1 text-sm font-bold">
                              बिल नंबर : {formData.propertyNumber ? formData.propertyNumber.toString().slice(-4) : '0000'}
                            </div>
                          </div>
                    
                          {/* Main Split */}
                          <div className="flex h-auto">
                            {/* Left Side */}
                            <div className="w-[65%] border-r border-black flex flex-col">
                              {/* Table Header */}
                              <div className="flex border-b border-black text-center text-sm font-bold bg-white h-auto">
                                <div className="w-[30%] border-r border-black flex items-center justify-center p-1">कराचे नाव</div>
                                <div className="w-[70%]">
                                  <div className="border-b border-black p-1">वसुल पात्र रकमा</div>
                                  <div className="flex">
                                    <div className="w-1/3 border-r border-black p-1">थकबाकी</div>
                                    <div className="w-1/3 border-r border-black p-1">चालू वर्ष</div>
                                    <div className="w-1/3 p-1">एकुण</div>
                                  </div>
                                </div>
                              </div>
                    
                              {/* Rows */}
                              <div className="flex-grow text-sm">
                                <div className="flex text-right py-0.5">
                                  <div className="w-[30%] text-left border-r border-black px-1">घरपट्टी</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{hPre || '-'}</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{hCur}</div>
                                  <div className="w-[23.4%] px-1">{hTot}</div>
                                </div>
                                <div className="flex text-right py-0.5">
                                  <div className="w-[30%] text-left border-r border-black px-1">विजकर</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{lPre || '-'}</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{lCur}</div>
                                  <div className="w-[23.4%] px-1">{lTot}</div>
                                </div>
                                <div className="flex text-right py-0.5">
                                  <div className="w-[30%] text-left border-r border-black px-1">आरोग्य कर</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{hlPre || '-'}</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{hlCur}</div>
                                  <div className="w-[23.4%] px-1">{hlTot}</div>
                                </div>
                                <div className="flex text-right py-0.5">
                                  <div className="w-[30%] text-left border-r border-black px-1">नोटिस फी</div>
                                  <div className="w-[23.3%] border-r border-black px-1">-</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{noticeFee || '-'}</div>
                                  <div className="w-[23.4%] px-1">{noticeFee || '-'}</div>
                                </div>
                                <div className="flex text-right py-0.5">
                                  <div className="w-[30%] text-left border-r border-black px-1">वॉरंट फी</div>
                                  <div className="w-[23.3%] border-r border-black px-1">-</div>
                                  <div className="w-[23.3%] border-r border-black px-1">{warrantFee || '-'}</div>
                                  <div className="w-[23.4%] px-1">{warrantFee || '-'}</div>
                                </div>
                                <div className="flex text-right py-0.5">
                                  <div className="w-[30%] text-left border-r border-black px-1 text-[11px] leading-tight">घरपट्टी थकबाकी दंड</div>
                                  <div className="w-[23.3%] border-r border-black px-1"></div>
                                  <div className="w-[23.3%] border-r border-black px-1"></div>
                                  <div className="w-[23.4%] px-1">{penalty}</div>
                                </div>
                                <div className="h-4 border-r-[0.5px] border-black ml-[30%]"></div>
                              </div>
                    
                              {/* Total Row */}
                              <div className="flex border-t border-black font-bold text-sm border-b border-black">
                                <div className="w-[30%] text-left border-r border-black px-1">एकुण</div>
                                <div className="w-[23.3%] text-right border-r border-black px-1">{totalArrears}</div>
                                <div className="w-[23.3%] text-right border-r border-black px-1">{totalCurrent}</div>
                                <div className="w-[23.4%] text-right px-1">{subTotal}</div>
                              </div>
                    
                              {/* Discount Row */}
                              <div className="flex border-b border-black text-xs font-semibold">
                                <div className="w-[76.6%] text-right border-r border-black px-1 py-1">
                                  दि.{discountDate} च्या आत संपूर्ण कर एकरकमी भरल्यास करातुन सुट
                                </div>
                                <div className="w-[23.4%] text-right px-1 py-1">{discount}</div>
                              </div>
                    
                              {/* Final Total */}
                              <div className="flex text-sm font-bold">
                                <div className="w-[76.6%] text-right border-r border-black px-1 py-1">एकुण</div>
                                <div className="w-[23.4%] text-right px-1 py-1">{finalTotal}</div>
                              </div>
                            </div>
                    
                            {/* Right Side */}
                            <div className="w-[35%] flex flex-col justify-between">
                              <div className="p-1 flex justify-between text-xs font-bold border-b border-transparent">
                                <span>मालमत्ता क्र. : {record.propertyNo || formData.propertyNumber}</span>
                                <span>बद्दल</span>
                              </div>
                              <div className="p-2 text-[11px] leading-snug text-justify font-serif">
                                <p>
                                  येथील ग्रामपंचायत यांजकडुन कळविण्यात येते की, आपणाकडे सदरहु ग्रामपंचायतची बाजुस दिलेल्या तपशिलाप्रमाणे रक्कम येणे निघत आहे.
                                  ती या बिलावरुन मागितली जात आहे तरी तुम्ही सदरहु रक्कम पंधरा दिवसाच्या आत म्हणजे
                                </p>
                                <div className="font-bold text-center my-1 tracking-widest bg-gray-100 py-0.5 border border-gray-300">
                                  {dueDate}
                                </div>
                                <p>
                                  भरणा करुन त्याची लेखी पावती घ्यावी. त्याप्रमाणे तुमच्याकडुन झाले नाही तर म. १९५८ चा ग्राम.का.क्र. १२९ (२) नियम क्र. (३) प्रमाणे तुम्हास नोटीस केली जाईल व पुढे जरु तर कायदेशीर कारवाई केली जाईल. कळावे.
                                </p>
                              </div>
                            </div>
                          </div>
                    
                          {/* Footer Section */}
                          <div className="border-t border-black">
                            <div className="p-1 border-b border-black font-bold text-sm">
                              अक्षरी रु. {numToMarathiWords(finalTotal)} रुपये फक्त
                            </div>
                            <div className="text-center p-1 text-[11px] font-bold">
                              बिलातील देय रक्कम पुर्ण भरल्याशिवाय कोणताही दाखला उतारा मिळणार नाही. आदेशावरुन
                            </div>
                            <div className="flex justify-between items-end px-2 pb-2 mt-2">
                              <div className="text-xs">
                                <p className="font-bold">दिनांक : {billDate}</p>
                                <p className="mt-6 font-bold">या बिलाची पोच स्थळ प्रतीवर घ्यावी.</p>
                              </div>
                              <div className="text-center text-xs">
                                <p className="mb-8 font-bold">ग्रामपंचायत अधिकारी</p>
                                <p className="font-bold">सरपंच</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                   })()}
                </div>

                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    style={{
                      background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                      color: 'white',
                      padding: '12px 32px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {language === 'marathi' ? '← मागे जा' : '← Go Back'}
                  </button>
                  <button
                    type="button"
                    onClick={handleNextStep}
                    style={{
                      background: 'linear-gradient(135deg, #4d7cfe 0%, #3b5fc9 100%)',
                      color: 'white',
                      padding: '12px 32px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {language === 'marathi' ? 'पेमेंट करा →' : 'Pay Now →'}
                  </button>
                </div>
             </div>
          )}

          {/* ─── Step 3: Payment Method ─── */}
          {currentStep === 3 && (
            <div style={formSectionStyle}>
              <h2 style={sectionTitleStyle}>{language === 'marathi' ? 'पेमेंट पद्धत' : 'Payment Method'}</h2>

              <div className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-blue-800">{language === 'marathi' ? 'एकूण रक्कम:' : 'Total Amount:'}</h3>
                      <p className="text-2xl font-bold text-green-700">₹ {formData.amount}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{language === 'marathi' ? 'मालमत्ता क्रमांक' : 'Property Number'}</p>
                      <p className="font-semibold">{formData.propertyNumber || '—'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-700 mb-4">{language === 'marathi' ? 'पेमेंट पद्धत निवडा:' : 'Select Payment Method:'}</h3>

                  <div style={paymentMethodStyle}>
                    {/* UPI Option */}
                    <div
                      style={paymentOptionStyle(formData.paymentMethod === 'upi')}
                      onClick={() => setFormData({ ...formData, paymentMethod: 'upi' })}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                          <span className="text-xl">📱</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{language === 'marathi' ? 'UPI पेमेंट' : 'UPI Payment'}</h4>
                          <p className="text-sm text-gray-600">Google Pay, PhonePe, Paytm</p>
                        </div>
                      </div>

                      {formData.paymentMethod === 'upi' && (
                        <div className="mt-5 space-y-5">
                          <div>
                            <label style={labelStyle}>
                              UPI ID <span style={requiredStar}>*</span>
                            </label>
                            <input
                              type="text"
                              name="upiId"
                              value={formData.upiId}
                              onChange={handleChange}
                              required
                              style={inputStyle}
                              placeholder={language === 'marathi' ? 'उदा: yourname@okhdfcbank' : 'e.g. yourname@okhdfcbank'}
                            />
                          </div>

                          {upiQRValue && (
                            <div className="flex flex-col items-center p-5 bg-white border rounded-xl shadow-sm">
                              <p className="font-semibold mb-3">{language === 'marathi' ? 'QR स्कॅन करा' : 'Scan QR'}</p>
                              <QRCodeSVG
                                value={upiQRValue}
                                size={220}
                                level="H"
                                fgColor="#000000"
                                bgColor="#ffffff"
                                marginSize={4}
                              />
                              <p className="mt-3 text-sm font-medium">
                                {language === 'marathi' ? 'रक्कम:' : 'Amount:'} ₹ {formData.amount}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formData.upiId}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  style={{
                    background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {language === 'marathi' ? '← मागे जा' : '← Go Back'}
                </button>

                <button
                  type="submit"
                  disabled={!formData.paymentMethod || isPaymentProcessing}
                  style={{
                    background:
                      !formData.paymentMethod || isPaymentProcessing
                        ? '#d1d5db'
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: formData.paymentMethod && !isPaymentProcessing ? 'pointer' : 'not-allowed',
                    opacity: formData.paymentMethod && !isPaymentProcessing ? 1 : 0.6,
                  }}
                >
                  {isPaymentProcessing 
                    ? (language === 'marathi' ? 'प्रक्रिया चालू आहे...' : 'Processing...') 
                    : (language === 'marathi' ? 'पेमेंट पूर्ण करा' : 'Complete Payment')}
                </button>
              </div>
            </div>
          )}
        </form>

        {/* Payment Success Screen */}
        {paymentSuccess && receiptData && (
          <div style={formSectionStyle}>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-green-600 mb-4">
                <span className="text-4xl">✅</span>
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">
                {language === 'marathi' ? 'पेमेंट यशस्वीरित्या पूर्ण झाले!' : 'Payment Successful!'}
              </h2>
              <p className="text-gray-600">
                {language === 'marathi' ? 'तुमच्या मोबाईलवर पुष्टीकरण संदेश पाठवला जाईल' : 'A confirmation message will be sent to your mobile'}
              </p>
            </div>

            {/* Receipt Details */}
            <div className="bg-white border-2 border-green-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-center mb-4 text-blue-800">
                🏠 {language === 'marathi' ? 'घरपट्टी पेमेंट रसीद' : 'House Tax Payment Receipt'}
              </h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b pb-2">
                  <span className="font-semibold text-gray-600">{language === 'marathi' ? 'रसीद क्रमांक:' : 'Receipt No.:'}</span>
                  <span className="font-mono">{receiptData.transactionId}</span>
                </div>
                
                <div className="flex justify-between border-b pb-2">
                  <span className="font-semibold text-gray-600">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
                  <span>{receiptData.paymentDate}</span>
                </div>
                
                <div className="flex justify-between border-b pb-2">
                  <span className="font-semibold text-gray-600">{language === 'marathi' ? 'वेळ:' : 'Time:'}</span>
                  <span>{receiptData.paymentTime}</span>
                </div>
                
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-bold text-gray-700 mb-2">{language === 'marathi' ? 'मालक माहिती:' : 'Owner Information:'}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'मालकाचे नाव:' : 'Owner Name:'}</span>
                      <p className="font-semibold">{receiptData.ownerName}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'मालमत्ता क्रमांक:' : 'Property Number:'}</span>
                      <p className="font-semibold">{receiptData.propertyNumber}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'मोबाईल क्रमांक:' : 'Mobile Number:'}</span>
                      <p className="font-semibold">{receiptData.mobileNumber}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">{language === 'marathi' ? 'वर्ष:' : 'Year:'}</span>
                      <p className="font-semibold">{receiptData.year}</p>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-bold text-gray-700 mb-2">{language === 'marathi' ? 'पेमेंट माहिती:' : 'Payment Information:'}</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{language === 'marathi' ? 'कर रक्कम:' : 'Tax Amount:'}</span>
                      <span className="font-bold text-green-700 text-lg">₹{receiptData.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{language === 'marathi' ? 'पेमेंट पद्धत:' : 'Payment Method:'}</span>
                      <span className="font-semibold">{receiptData.paymentMethod.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-center text-xs text-gray-500 mt-4 pt-3 border-t">
                  <p>{language === 'marathi' ? 'ही रसीद कंप्युटरद्वारे निर्मित आहे' : 'This receipt is computer generated'}</p>
                  <p>{language === 'marathi' ? 'कृपया ती सुरक्षित ठेवा' : 'Please keep it safe'}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleDownloadReceipt}
                className="flex items-center justify-center gap-2 w-full sm:w-auto shadow-md hover:shadow-lg transition-shadow"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span>📄</span>
                <span>{language === 'marathi' ? 'रसीड डाउनलोड करा' : 'Download Receipt'}</span>
              </button>
              
              <button
                onClick={handleNewPayment}
                className="flex items-center justify-center gap-2 w-full sm:w-auto shadow-md hover:shadow-lg transition-shadow"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span>🔄</span>
                <span>{language === 'marathi' ? 'नवीन पेमेंट करा' : 'Make New Payment'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HouseTax;